// Number and currency formatting utilities

export const formatCurrency = (amount, currency = '₹') => {
  if (!amount && amount !== 0) return `${currency}0`;
  
  const num = parseFloat(amount);
  if (isNaN(num)) return `${currency}0`;
  
  // Use Indian number formatting for currency
  const formatted = formatIndianNumber(num);
  return `${currency}${formatted}`;
};

export const formatNumber = (number, decimals = 0) => {
  if (!number && number !== 0) return '0';
  
  const num = parseFloat(number);
  if (isNaN(num)) return '0';
  
  // Use Indian number formatting with proper comma placement
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Specific function for Indian currency formatting with commas
export const formatIndianNumber = (number) => {
  if (!number && number !== 0) return '0';
  
  const num = parseFloat(number);
  if (isNaN(num)) return '0';
  
  // Convert to string and add Indian comma formatting manually for better control
  const numStr = Math.round(num).toString();
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNumbers = numStr.substring(0, numStr.length - 3);
  
  if (otherNumbers !== '') {
    return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
  } else {
    return lastThree;
  }
};

export const formatPercentage = (value, decimals = 2) => {
  if (!value && value !== 0) return '0%';
  
  const num = parseFloat(value);
  if (isNaN(num)) return '0%';
  
  return `${num.toFixed(decimals)}%`;
};

export const parseCurrency = (value) => {
  if (!value) return 0;
  
  // Remove currency symbols and formatting
  const cleaned = value.toString()
    .replace(/[₹$,\s]/g, '')
    .replace(/[LlKkCcRr]/g, '');
  
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

export const validateNumber = (value, min = 0, max = Infinity) => {
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  return num >= min && num <= max;
};

export const validateRequired = (value) => {
  return value !== null && value !== undefined && value.toString().trim() !== '';
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePAN = (pan) => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan);
};

export const formatPAN = (value) => {
  // Remove non-alphanumeric characters and convert to uppercase
  const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  
  // Format as AAAAA9999A
  let formatted = '';
  for (let i = 0; i < Math.min(cleaned.length, 10); i++) {
    if (i === 5) formatted += cleaned[i]; // First digit
    else if (i < 5) formatted += cleaned[i]; // Letters
    else if (i < 9) formatted += cleaned[i]; // Digits
    else formatted += cleaned[i]; // Last letter
  }
  
  return formatted;
};

export const calculateAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

export const getFinancialYear = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // JavaScript months are 0-indexed
  
  if (month >= 4) {
    return `${year}-${(year + 1).toString().slice(-2)}`;
  } else {
    return `${year - 1}-${year.toString().slice(-2)}`;
  }
};

export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '';
  
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  
  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    default:
      return `${day}/${month}/${year}`;
  }
};

// -------- Validation helpers for invoice generator --------

// Validate ISO date string YYYY-MM-DD and ensure it's a real date
export const validateISODate = (dateStr) => {
  if (!dateStr) return false;
  const patternOk = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  if (!patternOk) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const [y, m, dd] = dateStr.split('-').map((x) => parseInt(x, 10));
  return d.getFullYear() === y && d.getMonth() + 1 === m && d.getDate() === dd;
};

// Compare two ISO dates; returns true if a <= b
export const isBeforeOrEqualDate = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return da.getTime() <= db.getTime();
};

// Validate Indian GSTIN (optional field: valid if empty)
export const validateGSTIN = (gstin) => {
  if (!gstin) return true;
  const val = gstin.toString().trim().toUpperCase();
  const re = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return re.test(val);
};

// Validate postal code based on country; India: 6 digits; otherwise loose alphanumeric
export const validatePostalCode = (country, code) => {
  if (!code) return true;
  const c = (country || '').toLowerCase();
  const v = code.toString().trim();
  if (c === 'india') return /^\d{6}$/.test(v);
  return /^[A-Za-z0-9\s-]{3,10}$/.test(v);
};

// Validate HSN/SAC code (optional, 4-8 alphanumeric)
export const validateHSN = (hsn) => {
  if (!hsn) return true;
  return /^[A-Za-z0-9]{4,8}$/.test(hsn.toString().trim());
};

// Validate IFSC code (optional)
export const validateIFSC = (ifsc) => {
  if (!ifsc) return true;
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toString().trim().toUpperCase());
};

// Validate UPI ID (optional, simple pattern)
export const validateUPI = (upi) => {
  if (!upi) return true;
  return /^[A-Za-z0-9][A-Za-z0-9._-]*@[A-Za-z0-9.-]+$/.test(upi.toString().trim());
};